import { MessageSquare, ArrowRight, Check, X, Clock, DollarSign } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { RFQNegotiation } from '@/api/services';
import { cn } from '@/lib/utils';

interface NegotiationThreadProps {
  negotiation: RFQNegotiation;
}

interface ThreadItemProps {
  party: 'shipper' | 'carrier';
  rate: number;
  currency?: string;
  message: string | null;
  timestamp: string;
  isLatest?: boolean;
  type: 'offer' | 'counter' | 'accept' | 'reject';
}

function ThreadItem({ party, rate, currency = 'USD', message, timestamp, isLatest, type }: ThreadItemProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isShipper = party === 'shipper';

  const typeConfig = {
    offer: { icon: DollarSign, label: 'Offer', color: 'text-blue-600' },
    counter: { icon: ArrowRight, label: 'Counter', color: 'text-amber-600' },
    accept: { icon: Check, label: 'Accepted', color: 'text-emerald-600' },
    reject: { icon: X, label: 'Rejected', color: 'text-red-600' },
  };

  const config = typeConfig[type];
  const Icon = config.icon;

  return (
    <div className={cn('flex gap-3', isShipper ? 'flex-row' : 'flex-row-reverse')}>
      {/* Avatar */}
      <div
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium',
          isShipper
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
            : 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
        )}
      >
        {isShipper ? 'S' : 'C'}
      </div>

      {/* Content */}
      <div className={cn('flex-1 max-w-[80%]', isShipper ? '' : 'text-right')}>
        <div
          className={cn(
            'inline-block rounded-lg p-3',
            isShipper
              ? 'bg-blue-50 dark:bg-blue-900/20'
              : 'bg-purple-50 dark:bg-purple-900/20'
          )}
        >
          <div className="flex items-center gap-2 mb-1">
            <Badge
              variant="outline"
              className={cn('text-xs', config.color)}
            >
              <Icon className="w-3 h-3 mr-1" />
              {config.label}
            </Badge>
            {isLatest && (
              <Badge variant="secondary" className="text-xs">
                Current
              </Badge>
            )}
          </div>

          <div className="font-semibold text-lg">
            {formatCurrency(rate)}
          </div>

          {message && (
            <p className="text-sm text-muted-foreground mt-1">
              "{message}"
            </p>
          )}
        </div>

        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {formatDate(timestamp)}
        </div>
      </div>
    </div>
  );
}

export function NegotiationThread({ negotiation }: NegotiationThreadProps) {
  const items: ThreadItemProps[] = [];

  // Add historical rounds from metadata if available
  const history = negotiation.metadata?.history || [];

  history.forEach((round) => {
    // Shipper's offer in this round
    items.push({
      party: 'shipper',
      rate: round.shipperRate,
      message: null,
      timestamp: round.timestamp,
      type: 'offer',
    });

    // Carrier's counter in this round (if exists)
    if (round.carrierCounter !== null) {
      items.push({
        party: 'carrier',
        rate: round.carrierCounter,
        message: round.carrierMessage,
        timestamp: round.timestamp,
        type: 'counter',
      });
    }
  });

  // Add current round's shipper offer
  items.push({
    party: 'shipper',
    rate: negotiation.targetRate,
    message: negotiation.message,
    timestamp: negotiation.createdAt,
    type: 'offer',
    isLatest: negotiation.status === 'Pending' || negotiation.status === 'InProgress',
  });

  // Add carrier response if exists
  if (negotiation.counterRate !== null && negotiation.status === 'CounterOffered') {
    items.push({
      party: 'carrier',
      rate: negotiation.counterRate,
      message: negotiation.counterMessage,
      timestamp: negotiation.respondedAt || negotiation.updatedAt,
      type: 'counter',
      isLatest: true,
    });
  }

  // Add final state if accepted or rejected
  if (negotiation.status === 'Accepted' && negotiation.finalRate) {
    items.push({
      party: 'carrier',
      rate: negotiation.finalRate,
      message: null,
      timestamp: negotiation.respondedAt || negotiation.updatedAt,
      type: 'accept',
      isLatest: true,
    });
  }

  if (negotiation.status === 'Rejected') {
    items.push({
      party: 'carrier',
      rate: negotiation.counterRate || negotiation.targetRate,
      message: negotiation.counterMessage,
      timestamp: negotiation.respondedAt || negotiation.updatedAt,
      type: 'reject',
      isLatest: true,
    });
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No negotiation activity yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {items.map((item, index) => (
        <ThreadItem key={index} {...item} />
      ))}
    </div>
  );
}
